/** Arabic-facing formatting. Content is Arabic; identifiers stay English. */

import type {Aspect, JobKind, SceneStatus, VisualSource} from './types';

const AR_DIGITS = '٠١٢٣٤٥٦٧٨٩';

/** Arabic-Indic digits, for numbers that read as product content rather than data. */
export const arNum = (n: number | string): string =>
  String(n).replace(/\d/g, (d) => AR_DIGITS[Number(d)]!);

/** Latin digits inside an Arabic locale — for clocks, ratios and file sizes. */
const AR_LATN = 'ar-EG-u-nu-latn';

export const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.valueOf())
    ? ''
    : d.toLocaleDateString(AR_LATN, {day: 'numeric', month: 'long', year: 'numeric'});
};

export const formatTime = (iso: string | Date): string => {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return Number.isNaN(d.valueOf())
    ? ''
    : d.toLocaleTimeString(AR_LATN, {hour: 'numeric', minute: '2-digit'});
};

/** "اليوم 9:04 م" for today, otherwise a full date — how the history list reads. */
export const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return '';
  return d.toDateString() === new Date().toDateString()
    ? `اليوم ${formatTime(d)}`
    : formatDate(iso);
};

/** m:ss.d — subtitle cue boundaries, which are floats in seconds. */
export const cueTime = (s: number): string => {
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(1).padStart(4, '0')}`;
};

/** m:ss — player position and duration. */
export const clockTime = (s: number): string => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

export const fileSize = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
};

export const initials = (name: string): string =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => `${w[0]}.`).join('');

export const ASPECT_DIMENSIONS: Record<Aspect, string> = {
  '9:16': '1080 × 1920',
  '16:9': '1920 × 1080',
  '1:1': '1080 × 1080',
};

/** CSS aspect-ratio, so every preview box matches the project's real output shape. */
export const ASPECT_CSS: Record<Aspect, string> = {
  '9:16': '9 / 16',
  '16:9': '16 / 9',
  '1:1': '1 / 1',
};

export const SOURCE_LABEL: Record<VisualSource, string> = {
  generated: 'مُولّد',
  archive: 'أرشيف',
  upload: 'مرفوع',
};

export const STATUS_LABEL: Record<SceneStatus, string> = {
  ready: 'جاهز',
  generating: 'جاري التوليد',
  failed: 'فشل',
  draft: 'مسودة',
};

export const STATUS_COLOR: Record<SceneStatus, string> = {
  ready: 'var(--state-ok)',
  generating: 'var(--state-work)',
  failed: 'var(--state-fail)',
  draft: 'var(--muted)',
};

export const JOB_LABEL: Record<JobKind, string> = {
  generate_concept: 'كتابة الفكرة الإبداعية',
  generate_script: 'تفكيك الموجز إلى مشاهد',
  generate_voiceover: 'توليد التعليق الصوتي',
  match_archive: 'البحث في الأرشيف',
  generate_visual: 'توليد المشاهد المرئية',
  render: 'تصدير الفيديو',
};

export const CONCEPT_FIELD_LABEL = {
  concept: 'الفكرة',
  hook: 'الخطّاف',
  narrative_direction: 'الاتجاه السردي',
  tone: 'النبرة',
} as const;

/** Project.status is a free string on the wire; map only what the API actually sets. */
export const projectStatus = (status: string): SceneStatus => {
  if (status === 'rendered') return 'ready';
  if (status === 'ready') return 'draft';  // ready = storyboard done, not yet rendered
  if (status === 'generating') return 'generating';
  if (status === 'failed') return 'failed';
  return 'draft';
};
